import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Building2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Share2,
  ShieldCheck,
  Stethoscope,
  Trash2,
} from "lucide-react";
import "./FacilitiesAdmin.css";
import FacilityForm, { type FacilityFormData } from "./FacilityForm";
import ShareRecordsDialog from "./ShareRecordsDialog";
import {
  addExistingMyFacility,
  createMyFacility,
  removeMyFacility,
  searchFacilities,
  type FacilitySearchResult,
  type FacilityType,
  type MyFacility,
  type Pet,
} from "../lib/api";

const facilityTypes: { value: FacilityType; label: string }[] = [
  { value: "VETERINARY_CLINIC", label: "Veterinary Clinic" },
  { value: "BOARDING_FACILITY", label: "Boarding Facility" },
  { value: "TRAINING_FACILITY", label: "Training Facility" },
  { value: "OTHER", label: "Other" },
];

export default function MyFacilitiesPage({
  facilities,
  pets,
  onFacilityCreated,
  onFacilityRemoved,
}: {
  facilities: MyFacility[];
  pets: Pet[];
  onFacilityCreated: (facility: MyFacility) => void;
  onFacilityRemoved: (facilityId: string) => void;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [showAdd, setShowAdd] = useState(false);
  const [removeError, setRemoveError] = useState('');
  const [shareFacility, setShareFacility] = useState<MyFacility | null>(null);

  const saveFacility = async (form: FacilityFormData) => {
    const token = await getAccessTokenSilently();
    if (!token) throw new Error("Missing access token");
    const facility = await createMyFacility(token, {
      name: form.name,
      type: form.type,
      contact_name: form.contact_name || null,
      email: form.email || null,
      phone: form.phone || null,
      website: form.website || null,
      address_line1: form.address_line1,
      address_line2: form.address_line2 || null,
      city: form.city,
      region: form.region,
      postal_code: form.postal_code,
      pet_ids: form.pet_ids,
      veterinarians: form.veterinarians
        .filter((vet) => vet.name.trim())
        .map((vet) => ({
          ...vet,
          name: vet.name.trim(),
          email: vet.email || null,
          phone: vet.phone || null,
          license_number: vet.license_number || null,
        })),
    });
    onFacilityCreated(facility);
    setShowAdd(false);
  };

  const searchExistingFacilities = async (
    query: string,
  ): Promise<FacilitySearchResult[]> => {
    const token = await getAccessTokenSilently();
    if (!token) throw new Error("Missing access token");
    return searchFacilities(token, query);
  };

  const addExistingFacility = async (facilityId: string, petIds: string[]) => {
    const token = await getAccessTokenSilently();
    if (!token) throw new Error("Missing access token");
    const facility = await addExistingMyFacility(token, facilityId, petIds);
    onFacilityCreated(facility);
    setShowAdd(false);
  };

  const removeFacility = async (facility: MyFacility) => {
    if (!window.confirm(`Remove ${facility.name} from My Facilities? The facility record will not be deleted.`)) return;
    setRemoveError('');
    try {
      const token = await getAccessTokenSilently();
      if (!token) throw new Error('Missing access token');
      await removeMyFacility(token, facility.id);
      onFacilityRemoved(facility.id);
    } catch (error) {
      setRemoveError(error instanceof Error ? error.message : 'Unable to remove this facility.');
    }
  };

  return (
    <>
      <div className="facility-page-heading">
        <div>
          <p className="eyebrow">Pet parent</p>
          <h1>My Facilities</h1>
          <p>Facilities your pets have used.</p>
        </div>
        <button
          className="button button-primary"
          onClick={() => setShowAdd(true)}
        >
          <Plus size={17} /> Add Facility
        </button>
      </div>
      {removeError && <p className="form-error facility-error" role="alert">{removeError}</p>}
      {facilities.length === 0 ? (
        <p className="facility-empty">No facilities added for your pets yet.</p>
      ) : (
        <div className="facility-list">
          {facilities.map((facility) => {
            const address = [
              facility.address_line1,
              facility.address_line2,
              facility.city,
              facility.region,
              facility.postal_code,
            ]
              .filter(Boolean)
              .join(", ");

            return (
            <section className="facility-row" key={facility.id}>
              <div className="facility-card-header my-facility-card-header">
                <div className="facility-mark">
                  <Building2 size={20} />
                </div>
                <div className="facility-copy">
                  <div className="my-facility-title-row">
                    <div className="facility-title">
                      <h2>{facility.name}</h2>
                      <span className="tag">
                        {facilityTypes.find(
                          (item) => item.value === facility.type,
                        )?.label || "Other"}
                      </span>
                      {facility.verified_at ? (
                        <span className="verified-badge">
                          <ShieldCheck size={13} /> Verified
                        </span>
                      ) : (
                        <span className="unverified-badge">Unverified</span>
                      )}
                    </div>
                    <div className="my-facility-actions">
                      <button type="button" className="button button-secondary" disabled={!facility.pets.length} onClick={() => setShareFacility(facility)} title={facility.pets.length ? 'Create a temporary records link' : 'Link a pet to this facility before sharing records'}><Share2 size={15} /> Share records</button>
                      <button type="button" className="button button-secondary my-facility-remove" onClick={() => void removeFacility(facility)}><Trash2 size={15} /> Remove</button>
                    </div>
                  </div>
                  <a
                    className="facility-address-link"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MapPin size={13} /> {address}
                  </a>
                </div>
              </div>
              <div className="facility-row-secondary my-facility-row-secondary">
                <div className="my-facility-details">
                  {(facility.phone || facility.email) && (
                    <div className="facility-meta">
                      {facility.phone && <span><Phone size={13} /> {facility.phone}</span>}
                      {facility.email && <span><Mail size={13} /> {facility.email}</span>}
                    </div>
                  )}
                  <div className="my-facility-veterinarians">
                    <span className="facility-meta my-facility-vet-count">
                      <Stethoscope size={13} /> {facility.veterinarians.length}{" "}
                      {facility.veterinarians.length === 1 ? "veterinarian" : "veterinarians"}
                    </span>
                    {facility.veterinarians.length > 0 && (
                      <div className="facility-vets my-facility-vets">
                        {facility.veterinarians.map((vet) => (
                          <span className="tag" key={vet.id || vet.name}>
                            {vet.name}
                            {vet.license_number ? ` · ${vet.license_number}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="facility-vets my-facility-pets">
                  {facility.pets.map((pet) => (
                    <span className="tag" key={pet.id}>
                      {pet.name}
                    </span>
                  ))}
                </div>
              </div>
            </section>
            );
          })}
        </div>
      )}
      {showAdd && (
        <FacilityForm
          mode="pet-parent"
          pets={pets}
          onClose={() => setShowAdd(false)}
          onSave={saveFacility}
          onSearchFacilities={searchExistingFacilities}
          onSaveExisting={addExistingFacility}
        />
      )}
      {shareFacility && <ShareRecordsDialog key={shareFacility.id} facility={shareFacility} onClose={() => setShareFacility(null)} />}
    </>
  );
}
